import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Collapse,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  Alert,
  Snackbar,
  Grid,
  Paper,
} from "@mui/material";
import {
  FaPlus,
  FaEye,
  FaPencilAlt,
  FaChevronDown,
  FaChevronUp,
  FaTimes,
  FaTrash,
} from "react-icons/fa";
import { MdCheckBoxOutlineBlank } from "react-icons/md";
import { auth, storage } from "../firebase";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import ActivityViewerPreview from "../components/ActivityViewerPreview";


// ─── helpers ────────────────────────────────────────────────────────────────
const BASE = "https://vol-backend.onrender.com";


const authedFetch = async (path, options = {}) => {
  const token = await auth.currentUser?.getIdToken(true);
  const res = await fetch(`${BASE}${path}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      ...(options.headers || {}),
    },
  });
  if (!res.ok) {
    const body = await res.text();
    console.error("❌ Backend error body:", body);
    throw new Error(`Error ${res.status} en ${path}`);
  }
  return res.json();
};

const TIPOS_ACTIVIDAD = [
  { value: "video", label: "🎬 Video", icon: "📹" },
  { value: "texto", label: "📄 Texto / Lectura", icon: "📄" },
  { value: "quiz", label: "📋 Quiz / Examen", icon: "📋" },
  { value: "documento", label: "📎 Carga de Documento", icon: "📎" },
  { value: "solicitud", label: "📝 Solicitud", icon: "📝" },
  { value: "scorm", label: "🖥️ SCORM", icon: "🖥️" },
  { value: "enlace", label: "🔗 Enlace externo", icon: "🔗" },
  { value: "upload",        label: "📤 Upload / Entrega",     icon: "📤" },
  { value: "lectura",       label: "📖 Lectura",              icon: "📖" },
  { value: "extraordinary", label: "⚡ Examen Extraordinario", icon: "⚡" },
];



const getTipoLabel = (tipo) =>
  TIPOS_ACTIVIDAD.find((t) => t.value === tipo)?.label || tipo;
const getTipoIcon = (tipo) =>
  TIPOS_ACTIVIDAD.find((t) => t.value === tipo)?.icon || "📌";


//---------------------------------INICIO MODAL EDITAR ACTIVIDAD--------------------------------------------------//
// ─── Modal: Editar / Crear Actividad ────────────────────────────────────────
function ModalEditarActividad({ open, onClose, actividad, moduloId, onSaved }) {
  const isNew = !actividad;

  const [form, setForm] = useState({
    title: "",
    type: "video",
    description: "",
    order: 1,
    xp: "",
    puntaje_minimo: "",
    duracion_estimada: "",
    activa: true,
    requerida: false,
    es_examen_final: false,
    orden_aleatorio: false,
    preguntas_mostrar: "",
    intentos_max: "",
    preguntas: [],
    video_url: "",
    contenido_html: "",
    enlace_url: "",
    scorm_url: "",
    manual_url: "",
    manual_file_name: "",
  });

  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  useEffect(() => {
    if (actividad) {
      setForm({
        title: actividad.title || actividad.name || actividad.nombre || "",
        type: actividad.type || actividad.tipo || "video",
        description: actividad.description || actividad.descripcion || "",
        order: actividad.order ?? actividad.order_index ?? 1,
        xp: actividad.xp ?? "",
        puntaje_minimo: actividad.min_score ?? actividad.puntaje_minimo ?? "",
        duracion_estimada: actividad.estimated_minutes ?? actividad.duracion_estimada ?? "",
        activa: actividad.is_active !== undefined ? !!actividad.is_active : actividad.activa !== false,
        requerida: actividad.required !== undefined ? !!actividad.required : !!actividad.requerida,
        es_examen_final: !!actividad.is_final_exam || !!actividad.es_examen_final,
        orden_aleatorio: actividad.config?.orden_aleatorio || false,
        preguntas_mostrar: actividad.config?.preguntas_mostrar ?? "",
        intentos_max: actividad.config?.intentos_max ?? "",
        preguntas: actividad.config?.preguntas || actividad.config?.questions || actividad.preguntas || [],
        video_url: actividad.config?.videoIds || actividad.config?.video_url || "",
        contenido_html: actividad.config?.contenido_html || actividad.config?.html || "",
        enlace_url: actividad.config?.enlace_url || actividad.config?.url || "",
        scorm_url: actividad.config?.scorm_url || actividad.config?.scormPackageUrl || "",
        manual_url: actividad.config?.manual_url || actividad.config?.url || actividad.config?.manualId || "",
        manual_file_name: actividad.config?.manual_file_name || "",
        video_url: String(actividad.config?.videoIds || actividad.config?.video_url || ""),
      });
    } else {
      setForm({
        title: "",
        type: "video",
        description: "",
        order: 1,
        xp: "",
        puntaje_minimo: "",
        duracion_estimada: "",
        activa: true,
        requerida: false,
        es_examen_final: false,
        orden_aleatorio: false,
        preguntas_mostrar: "",
        intentos_max: "",
        preguntas: [],
        video_url: "",
        contenido_html: "",
        enlace_url: "",
        scorm_url: "",
      });
    }
    setError(null);
    setConfirmDelete(false);
  }, [actividad, open]);

  const handleChange = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  const addPregunta = () =>
    setForm((f) => ({
      ...f,
      preguntas: [
        ...f.preguntas,
        { enunciado: "", opciones: ["", "", ""], correcta: null },
      ],
    }));

  const removePregunta = (idx) =>
    setForm((f) => ({
      ...f,
      preguntas: f.preguntas.filter((_, i) => i !== idx),
    }));

  const updatePregunta = (idx, field, value) =>
    setForm((f) => {
      const updated = [...f.preguntas];
      updated[idx] = { ...updated[idx], [field]: value };
      return { ...f, preguntas: updated };
    });

  const updateOpcion = (pregIdx, opIdx, value) =>
    setForm((f) => {
      const updated = [...f.preguntas];
      const opciones = [...updated[pregIdx].opciones];
      opciones[opIdx] = value;
      updated[pregIdx] = { ...updated[pregIdx], opciones };
      return { ...f, preguntas: updated };
    });

  const handleSave = async () => {
    if (!form.title.trim()) {
      setError("El nombre de la actividad es requerido.");
      return;
    }

    if (form.type === "texto" && !form.manual_url?.trim() && !form.contenido_html?.trim()) {
      setError("Para una actividad de tipo Texto debes subir un PDF/imagen o agregar contenido HTML.");
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const codigoActividad = `${form.type.substring(0, 3).toUpperCase()}_${Date.now().toString().slice(-6)}_${Math.floor(Math.random() * 99)}`;

    const payload = {
      ...(isNew && { code: codigoActividad }),
      title: form.title,
      type: form.type,  // ← siempre se manda, tanto en POST como en PATCH
      description: form.description || "",
      order: Number(form.order) || 1,
      isActive: form.activa,
      required: form.requerida,
    };

      if (form.xp !== "" && form.xp !== null) payload.xp = Number(form.xp);
      if (form.puntaje_minimo !== "" && form.puntaje_minimo !== null) payload.minScore = Number(form.puntaje_minimo);
      if (form.duracion_estimada !== "" && form.duracion_estimada !== null) payload.estimatedMinutes = Number(form.duracion_estimada);
      if (form.es_examen_final) payload.isFinalExam = true;

      if (form.type === "quiz") {
        payload.config = {
          orden_aleatorio: form.orden_aleatorio,
          preguntas_mostrar: form.preguntas_mostrar !== "" ? Number(form.preguntas_mostrar) : undefined,
          intentos_max: form.intentos_max !== "" ? Number(form.intentos_max) : undefined,
          preguntas: form.preguntas,
        };
      } else if (form.type === "video" && form.video_url?.trim()) {
        payload.config = { 
          videoIds: form.video_url.trim(),
          video_url: form.video_url.trim(), // ✅ guarda ambos para compatibilidad
        };
      } else if (form.type === "texto") {
        payload.config = {
          ...(form.manual_url?.trim() && {
            url: form.manual_url.trim(),
            manual_url: form.manual_url.trim(),
          }),
          ...(form.manual_file_name?.trim() && {
            manual_file_name: form.manual_file_name.trim(),
          }),
          ...(form.contenido_html?.trim() && {
            html: form.contenido_html.trim(),
            contenido_html: form.contenido_html.trim(),
          }),
        };
      } else if (form.type === "enlace" && form.enlace_url?.trim()) {
        payload.config = { enlace_url: form.enlace_url, url: form.enlace_url, };
      } else if (form.type === "scorm" && form.scorm_url?.trim()) {
        payload.config = { scorm_url: form.scorm_url };
      } else if (form.type === "documento" || form.type === "upload" || form.type === "solicitud") {
        payload.config = {};
      } else if (form.type === "extraordinary") {
        payload.config = {
          orden_aleatorio: form.orden_aleatorio,
          preguntas_mostrar: form.preguntas_mostrar !== "" ? Number(form.preguntas_mostrar) : undefined,
          intentos_max: form.intentos_max !== "" ? Number(form.intentos_max) : undefined,
          preguntas: form.preguntas,
        };
      }

      console.log("📦 Payload actividad:", JSON.stringify(payload, null, 2));
      console.log("📍 moduloId:", moduloId);

      if (isNew) {
        await authedFetch(`/admin/catalog/modules/${moduloId}/activities`, {
          method: "POST",
          body: JSON.stringify(payload),
        });
      } else {
        await authedFetch(`/admin/catalog/activities/${actividad.id}`, {
          method: "PATCH",
          body: JSON.stringify(payload),
        });
      }
      onSaved?.();
      onClose();
    } catch (err) {
      console.error("❌ Error:", err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }
    setDeleting(true);
    setError(null);
    try {
      await authedFetch(`/admin/catalog/activities/${actividad.id}`, { method: "DELETE" });
      onSaved?.();
      onClose();
    } catch (err) {
      setError(err.message);
      setConfirmDelete(false);
    } finally {
      setDeleting(false);
    }
  };

  const LETTER = ["A", "B", "C", "D", "E"];

  return (
    <>
      {/* Dialog principal */}
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, maxHeight: "90vh" } }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pb: 1, pt: 2.5, px: 3,
            borderBottom: "1px solid #f0f0f0",
          }}
        >
          <Typography sx={{ fontWeight: 800, fontSize: "1.1rem", color: "#111" }}>
            {isNew ? "Nueva Actividad" : "Editar Actividad"}
          </Typography>
          <IconButton onClick={onClose} size="small" sx={{ color: "#999" }}>
            <FaTimes size={14} />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ px: 3, py: 2, overflowY: "auto" }}>
          <Alert
            severity="info"
            icon={<span style={{ fontSize: "1rem" }}>💡</span>}
            sx={{
              mb: 2.5, backgroundColor: "#fffbea", color: "#555",
              border: "1px solid #ffe082", borderRadius: 2, fontSize: "0.8rem",
              "& .MuiAlert-message": { lineHeight: 1.5 },
            }}
          >
            Una <strong>actividad</strong> es el contenido concreto que realiza el voluntario: ver un
            video, leer un documento, contestar un quiz, subir una evidencia, etc.
          </Alert>

          {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}

          <TextField
            label="Nombre de la actividad *"
            fullWidth size="small" value={form.title}
            onChange={(e) => handleChange("title", e.target.value)}
            helperText="Nombre visible que verá el voluntario en la plataforma."
            sx={{ mb: 2 }}
          />

          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>Tipo de actividad</InputLabel>
            <Select label="Tipo de actividad" value={form.type} onChange={(e) => handleChange("type", e.target.value)}>
              {TIPOS_ACTIVIDAD.map((t) => (
                <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Descripción" fullWidth size="small" multiline rows={2}
            value={form.description} onChange={(e) => handleChange("description", e.target.value)}
            helperText="Instrucción breve que orienta al voluntario sobre qué debe hacer."
            sx={{ mb: 2 }}
          />

          <Box sx={{ display: "flex", gap: 1.5, mb: 1 }}>
            <TextField label="Orden" size="small" type="number" value={form.order}
              onChange={(e) => handleChange("order", e.target.value)}
              helperText="Posición en el módulo." sx={{ flex: 1 }} />
            <TextField label="XP" size="small" type="number" value={form.xp}
              onChange={(e) => handleChange("xp", e.target.value)}
              helperText="Puntos al completar." sx={{ flex: 1 }} />
            <TextField label="Puntaje mínimo" size="small" type="number" value={form.puntaje_minimo}
              onChange={(e) => handleChange("puntaje_minimo", e.target.value)}
              helperText="Solo para quizzes." sx={{ flex: 1 }} />
          </Box>

          <TextField
            label="Duración estimada (minutos)" fullWidth size="small" type="number"
            value={form.duracion_estimada} onChange={(e) => handleChange("duracion_estimada", e.target.value)}
            helperText="Tiempo aproximado que tarda el voluntario en completar esta actividad."
            sx={{ mb: 2 }}
          />

          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2 }}>
            <FormControlLabel
              control={<Switch checked={form.activa} onChange={(e) => handleChange("activa", e.target.checked)} size="small" />}
              label={<Typography sx={{ fontSize: "0.85rem" }}>Activa</Typography>}
            />
            <FormControlLabel
              control={<Switch checked={form.requerida} onChange={(e) => handleChange("requerida", e.target.checked)} size="small" />}
              label={<Typography sx={{ fontSize: "0.85rem" }}>Requerida para avanzar</Typography>}
            />
            
            <FormControlLabel
              control={
                <Switch
                  checked={form.es_examen_final}
                  onChange={(e) => {
                    // Si ya estaba activo cuando se abrió el modal, no permitir desactivar
                    if (actividad?.is_final_exam && !e.target.checked) return;
                    handleChange("es_examen_final", e.target.checked);
                  }}
                  disabled={!!actividad?.is_final_exam}
                  size="small"
                />
              }
              label={
                <Typography sx={{ fontSize: "0.85rem" }}>
                  Es examen final del módulo
                  {actividad?.is_final_exam && (
                    <Typography component="span" sx={{ fontSize: "0.72rem", color: "#999", ml: 0.5 }}>
                      (ya no se puede desactivar)
                    </Typography>
                  )}
                </Typography>
              }
            />
          </Box>

          <Divider sx={{ mb: 2 }} />

          <Typography sx={{ fontWeight: 700, fontSize: "0.9rem", color: "#444", mb: 1.5 }}>
            ⚙️ Configuración — {getTipoLabel(form.type)}
          </Typography>

          {form.type === "video" && (
            <TextField label="URL del video" fullWidth size="small" value={form.video_url}
              onChange={(e) => handleChange("video_url", e.target.value)}
              placeholder="https://youtube.com/watch?v=..."
              helperText="URL de YouTube, Vimeo u otro reproductor." sx={{ mb: 1 }} />
          )}
          {form.type === "texto" && (
            <Box>
              <Button
                variant="outlined"
                component="label"
                disabled={form._uploading}
                sx={{ borderRadius: 999, textTransform: "none", mb: 1 }}
              >
                {form._uploading
                  ? "Subiendo..."
                  : form.manual_file_name || "Elegir PDF o imagen"}
                <input
                  type="file"
                  hidden
                  accept=".pdf,image/*"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    handleChange("_uploading", true);
                    try {
                      const user = auth.currentUser;
                      const path = `recursos/${user.uid}/${Date.now()}-${file.name}`;
                      const storageRef = ref(storage, path);
                      await uploadBytes(storageRef, file);
                      const url = await getDownloadURL(storageRef);
                      setForm((f) => ({
                        ...f,
                        manual_url: url,
                        manual_file_name: file.name,
                        _uploading: false,
                      }));
                    } catch (err) {
                      setForm((f) => ({ ...f, _uploading: false }));
                      setError("No se pudo subir el archivo: " + err.message);
                    }
                  }}
                />
              </Button>

              {form.manual_url && (
                <Typography variant="caption" sx={{ color: "green", display: "block", mb: 1 }}>
                  ✅ {form.manual_file_name || "Archivo subido"}
                </Typography>
              )}
              <TextField
                label="Contenido (HTML o texto)"
                fullWidth size="small" multiline rows={3}
                value={form.contenido_html}
                onChange={(e) => handleChange("contenido_html", e.target.value)}
                helperText="Opcional. Texto o HTML adicional que acompaña al recurso."
                sx={{ mb: 1 }}
              />
            </Box>
          )}

          {form.type === "enlace" && (
            <TextField label="URL del enlace" fullWidth size="small" value={form.enlace_url}
              onChange={(e) => handleChange("enlace_url", e.target.value)}
              placeholder="https://..." helperText="Enlace externo al que se redirigirá al voluntario." sx={{ mb: 1 }} />
          )}
          {form.type === "scorm" && (
            <TextField label="URL del paquete SCORM" fullWidth size="small" value={form.scorm_url}
              onChange={(e) => handleChange("scorm_url", e.target.value)}
              placeholder="https://..." helperText="URL del index del paquete SCORM desplegado." sx={{ mb: 1 }} />
          )}
          {(form.type === "documento" || form.type === "solicitud") && (
            <Alert severity="info" sx={{ borderRadius: 2, fontSize: "0.8rem" }}>
              {form.type === "documento"
                ? "El voluntario podrá subir un archivo desde esta actividad. No requiere configuración adicional."
                : "Esta actividad generará una solicitud que el staff debe aprobar. No requiere configuración adicional."}
            </Alert>
          )}

          {(form.type === "quiz" || form.type === "extraordinary") && (
            <Box>
              <Box sx={{ display: "flex", gap: 1.5, mb: 2, alignItems: "center", flexWrap: "wrap" }}>
                <FormControlLabel
                  control={<Switch checked={form.orden_aleatorio} onChange={(e) => handleChange("orden_aleatorio", e.target.checked)} size="small" />}
                  label={<Typography sx={{ fontSize: "0.82rem" }}>Orden aleatorio de preguntas</Typography>}
                />
                <TextField label="Preguntas a mostrar" size="small" type="number" value={form.preguntas_mostrar}
                  onChange={(e) => handleChange("preguntas_mostrar", e.target.value)}
                  helperText="Dejar vacío = mostrar todas" sx={{ width: 160 }} />
                <TextField label="Intentos máx." size="small" type="number" value={form.intentos_max}
                  onChange={(e) => handleChange("intentos_max", e.target.value)}
                  helperText="Dejar vacío = sin límite" sx={{ width: 150 }} />
              </Box>

              {form.preguntas.map((preg, pIdx) => (
                <Box key={pIdx} sx={{ border: "1px solid #e8e8e8", borderRadius: 2, p: 2, mb: 2, backgroundColor: "#fafafa" }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: "0.85rem", color: "#333" }}>Pregunta {pIdx + 1}</Typography>
                    <IconButton size="small" onClick={() => removePregunta(pIdx)} sx={{ color: "#ccc", "&:hover": { color: "#e60000" } }}>
                      <FaTimes size={12} />
                    </IconButton>
                  </Box>
                  <TextField label="Enunciado" fullWidth size="small" value={preg.enunciado}
                    onChange={(e) => updatePregunta(pIdx, "enunciado", e.target.value)} sx={{ mb: 1.5 }} />
                  {preg.opciones.map((op, oIdx) => (
                    <Box key={oIdx} sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.8 }}>
                      <Button
                        size="small" variant={preg.correcta === oIdx ? "contained" : "outlined"}
                        onClick={() => updatePregunta(pIdx, "correcta", oIdx)}
                        sx={{
                          minWidth: 32, width: 32, height: 32, p: 0, fontWeight: 800, fontSize: "0.75rem", borderRadius: 1,
                          backgroundColor: preg.correcta === oIdx ? "#e60000" : "transparent",
                          color: preg.correcta === oIdx ? "#fff" : "#999",
                          borderColor: preg.correcta === oIdx ? "#e60000" : "#ddd",
                          "&:hover": { backgroundColor: preg.correcta === oIdx ? "#b30000" : "#fff5f5", borderColor: "#e60000", color: preg.correcta === oIdx ? "#fff" : "#e60000" },
                        }}
                      >{LETTER[oIdx]}</Button>
                      <TextField fullWidth size="small" value={op}
                        onChange={(e) => updateOpcion(pIdx, oIdx, e.target.value)}
                        placeholder={`Opción ${LETTER[oIdx]}`} />
                    </Box>
                  ))}
                  <Typography sx={{ fontSize: "0.72rem", color: "#aaa", mt: 0.5 }}>
                    Haz clic en la letra para marcarla como correcta.
                  </Typography>
                  {preg.opciones.length < 5 && (
                    <Button size="small"
                      sx={{ mt: 1, fontSize: "0.75rem", color: "#e60000", textTransform: "none" }}
                      onClick={() => updatePregunta(pIdx, "opciones", [...preg.opciones, ""])}>
                      + Agregar opción
                    </Button>
                  )}
                </Box>
              ))}
              <Button size="small" startIcon={<FaPlus size={10} />} onClick={addPregunta}
                sx={{ color: "#e60000", textTransform: "none", fontWeight: 600, fontSize: "0.82rem" }}>
                AGREGAR PREGUNTA
              </Button>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #f0f0f0", gap: 1, justifyContent: "space-between" }}>
          <Box>
            {!isNew && (
              confirmDelete ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography sx={{ fontSize: "0.78rem", color: "#e60000", fontWeight: 600 }}>¿Confirmar eliminación?</Typography>
                  <Button size="small" variant="contained" onClick={handleDelete} disabled={deleting}
                    sx={{ backgroundColor: "#e60000", color: "#fff", textTransform: "none", fontWeight: 700, fontSize: "0.78rem", minWidth: 80, "&:hover": { backgroundColor: "#b30000" } }}>
                    {deleting ? <CircularProgress size={13} sx={{ color: "#fff" }} /> : "Sí, eliminar"}
                  </Button>
                  <Button size="small" onClick={() => setConfirmDelete(false)}
                    sx={{ textTransform: "none", fontSize: "0.78rem", color: "#777" }}>Cancelar</Button>
                </Box>
              ) : (
                <Tooltip title="Eliminar actividad">
                  <Button size="small" startIcon={<FaTrash size={11} />} onClick={handleDelete}
                    sx={{ textTransform: "none", fontWeight: 600, color: "#cc0000", borderColor: "#ffd0d0", fontSize: "0.82rem", border: "1px solid #ffd0d0", borderRadius: 1.5, "&:hover": { backgroundColor: "#fff5f5", borderColor: "#e60000" } }}>
                    Eliminar
                  </Button>
                </Tooltip>
              )
            )}
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<FaEye size={12} />}
              onClick={() => setPreviewOpen(true)}
              sx={{ textTransform: "none", fontWeight: 600, color: "#555", borderColor: "#ddd", fontSize: "0.85rem", "&:hover": { borderColor: "#999" } }}>
              VISTA PREVIA
            </Button>
            <Button variant="contained" onClick={handleSave} disabled={saving}
              sx={{ textTransform: "none", fontWeight: 700, backgroundColor: "#e60000", color: "#fff", fontSize: "0.85rem", "&:hover": { backgroundColor: "#b30000" } }}>
              {saving && <CircularProgress size={16} sx={{ color: "#fff", mr: 1 }} />}
              ✓ CONFIRMAR Y GUARDAR
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* ✅ Dialog de vista previa — CORRECTAMENTE UBICADO, FUERA DEL Dialog principal */}
      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, maxHeight: "90vh" } }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pb: 1, pt: 2, px: 3, borderBottom: "1px solid #f0f0f0" }}>
          <Typography sx={{ fontWeight: 800, fontSize: "1rem", color: "#111" }}>
            👁️ Vista previa — {form.title || "Sin título"}
          </Typography>
          <IconButton onClick={() => setPreviewOpen(false)} size="small" sx={{ color: "#999" }}>
            <FaTimes size={14} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 3, py: 2.5 }}>
          <ActivityViewerPreview form={form} />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #f0f0f0" }}>
          <Button variant="outlined" onClick={() => setPreviewOpen(false)}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
//---------------------------------FIN MODAL EDITAR ACTIVIDAD-----------------------------------------------------//

//---------------------------------INICIO MODAL EDITAR BLOQUE, CREAR BLOQUE---------------------------------------//
function ModalEditarBloque({ open, onClose, bloque, programaCode, onSaved }) {
  const isNew = !bloque;

  const [form, setForm] = useState({
    title: "", code: "", descripcion: "", orden: 1,
    duracion_estimada: "", activo: true, opcional: false,
    has_practical_exam: false,
    has_block_extraordinary: false,
    has_block_practical_extra: false,
    weight_teoria: 0.40,
    weight_practica: 0.60,
    min_passing_grade: 8.00,
  });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  useEffect(() => {
    if (bloque) {
      setForm({
        title: bloque.title || bloque.nombre || bloque.name || "",
        code: bloque.code || bloque.codigo || "",
        descripcion: bloque.description || bloque.descripcion || "",
        orden: bloque.order ?? bloque.orden ?? 1,
        duracion_estimada: bloque.estimated_minutes ?? bloque.duracion_estimada ?? "",
        activo: bloque.is_active !== undefined ? !!bloque.is_active : bloque.activo !== false,
        opcional: !!bloque.optional || !!bloque.opcional,
        // Nuevos campos
        has_practical_exam: bloque.has_practical_exam === 1,
        has_block_extraordinary: bloque.has_block_extraordinary === 1,
        has_block_practical_extra: bloque.has_block_practical_extra === 1,
        weight_teoria: bloque.weight_teoria ?? 0.40,
        weight_practica: bloque.weight_practica ?? 0.60,
        min_passing_grade: bloque.min_passing_grade ?? 8.00,
      });
    } else {
      setForm({ 
        title: "", code: "", descripcion: "", orden: 1, duracion_estimada: "", 
        activo: true, opcional: false,
        has_practical_exam: false, has_block_extraordinary: false, has_block_practical_extra: false,
        weight_teoria: 0.40, weight_practica: 0.60, min_passing_grade: 8.00,
      });
    }
    setError(null);
    setConfirmDelete(false);
  }, [bloque, open]);

  const handleChange = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  const handleSave = async () => {
    if (!form.title.trim()) { setError("El nombre del bloque es requerido."); return; }
    setSaving(true);
    setError(null);
    try {
      const payload = {
        title: form.title,
        code: form.code || undefined,
        description: form.descripcion,
        order: Number(form.orden) || 1,
        estimatedMinutes: form.duracion_estimada !== "" ? Number(form.duracion_estimada) : undefined,
        isActive: form.activo,
        optional: form.opcional,
        has_practical_exam: form.has_practical_exam ? 1 : 0,
        has_block_extraordinary: form.has_block_extraordinary ? 1 : 0,
        has_block_practical_extra: form.has_block_practical_extra ? 1 : 0,
        weight_teoria: form.weight_teoria,
        weight_practica: form.weight_practica,
        min_passing_grade: form.min_passing_grade,
      };

      if (isNew) {
        if (!programaCode) throw new Error("No se ha especificado un programa para este bloque");
        await authedFetch(`/admin/catalog/programs/${programaCode}/blocks`, {
          method: "POST",
          body: JSON.stringify(payload),
        });
      } else {
        if (!bloque?.id) throw new Error("No se ha especificado el ID del bloque a editar");
        await authedFetch(`/admin/catalog/blocks/${bloque.id}`, {
          method: "PATCH",
          body: JSON.stringify(payload),
        });
      }
      onSaved?.();
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) { setConfirmDelete(true); return; }
    setDeleting(true);
    setError(null);
    try {
      await authedFetch(`/admin/catalog/blocks/${bloque.id}`, { method: "DELETE" });
      onSaved?.();
      onClose();
    } catch (err) {
      setError(err.message);
      setConfirmDelete(false);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pb: 1, pt: 2.5, px: 3, borderBottom: "1px solid #f0f0f0" }}>
          <Typography sx={{ fontWeight: 800, fontSize: "1.1rem", color: "#111" }}>
            {isNew ? "📋 Nuevo Bloque" : "📋 Editar Bloque"}
          </Typography>
          <IconButton onClick={onClose} size="small" sx={{ color: "#999" }}><FaTimes size={14} /></IconButton>
        </DialogTitle>

        <DialogContent sx={{ px: 3, py: 2.5 }}>
          <Alert severity="info" icon={<span style={{ fontSize: "1rem" }}>💡</span>}
            sx={{ mb: 2.5, backgroundColor: "#fffbea", color: "#555", border: "1px solid #ffe082", borderRadius: 2, fontSize: "0.8rem", "& .MuiAlert-message": { lineHeight: 1.5 } }}>
            Un <strong>bloque</strong> agrupa módulos relacionados dentro del programa.
          </Alert>

          {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}

          {isNew && (
            <TextField label="Código del bloque *" fullWidth size="small" value={form.code}
              onChange={(e) => handleChange("code", e.target.value)}
              helperText="Sigue la convención del programa: B01, B02, B03…" sx={{ mb: 2 }} />
          )}

          <TextField label="Nombre del bloque *" fullWidth size="small" value={form.title}
            onChange={(e) => handleChange("title", e.target.value)}
            helperText="Nombre visible que describe el grupo de contenidos." sx={{ mb: 2 }} />

          <TextField label="Descripción" fullWidth size="small" multiline rows={2} value={form.descripcion}
            onChange={(e) => handleChange("descripcion", e.target.value)}
            helperText="Opcional. Explica qué cubre este bloque." sx={{ mb: 2 }} />

          <TextField label="Orden" size="small" type="number" value={form.orden}
            onChange={(e) => handleChange("orden", e.target.value)}
            helperText="Posición del bloque dentro del programa." sx={{ mb: 2, width: "100%" }} />

          <TextField label="Duración estimada (minutos)" fullWidth size="small" type="number"
            value={form.duracion_estimada} onChange={(e) => handleChange("duracion_estimada", e.target.value)}
            helperText="Tiempo aproximado para completar todos los módulos de este bloque." sx={{ mb: 2 }} />

          {/* ── Evaluaciones especiales del bloque ── */}
          <Box sx={{ mt: 2, mb: 1 }}>
            <Typography sx={{ fontWeight: 700, fontSize: "0.85rem", color: "#444", mb: 1 }}>
              Evaluaciones especiales
            </Typography>
            
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <FormControlLabel
                control={
                  <Switch 
                    checked={form.has_practical_exam} 
                    onChange={(e) => handleChange("has_practical_exam", e.target.checked)} 
                    size="small" 
                  />
                }
                label={<Typography sx={{ fontSize: "0.85rem" }}>Tiene examen práctico</Typography>}
              />

              <FormControlLabel
                control={
                  <Switch 
                    checked={form.has_block_extraordinary} 
                    onChange={(e) => handleChange("has_block_extraordinary", e.target.checked)} 
                    size="small" 
                  />
                }
                label={<Typography sx={{ fontSize: "0.85rem" }}>Tiene extraordinario teórico de bloque</Typography>}
              />

              <FormControlLabel
                control={
                  <Switch 
                    checked={form.has_block_practical_extra} 
                    onChange={(e) => handleChange("has_block_practical_extra", e.target.checked)} 
                    size="small" 
                  />
                }
                label={<Typography sx={{ fontSize: "0.85rem" }}>Tiene extraordinario práctico</Typography>}
              />
            </Box>
          </Box>

          {/* ── Pesos y nota mínima (solo si tiene práctica) ── */}
          {form.has_practical_exam && (
            <Box sx={{ mt: 2, p: 2, backgroundColor: "#fafafa", borderRadius: 2 }}>
              <Typography sx={{ fontWeight: 700, fontSize: "0.85rem", color: "#444", mb: 1.5 }}>
                Configuración de evaluación
              </Typography>
              
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontSize: "0.75rem", color: "#666", mb: 0.5 }}>Peso teoría</Typography>
                  <TextField
                    size="small"
                    type="number"
                    value={form.weight_teoria}
                    onChange={(e) => handleChange("weight_teoria", parseFloat(e.target.value))}
                    inputProps={{ min: 0, max: 1, step: 0.05 }}
                    sx={{ width: "100%" }}
                  />
                  <Typography sx={{ fontSize: "0.65rem", color: "#999", mt: 0.3 }}>default 0.40</Typography>
                </Box>
                
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontSize: "0.75rem", color: "#666", mb: 0.5 }}>Peso práctica</Typography>
                  <TextField
                    size="small"
                    type="number"
                    value={form.weight_practica}
                    onChange={(e) => handleChange("weight_practica", parseFloat(e.target.value))}
                    inputProps={{ min: 0, max: 1, step: 0.05 }}
                    sx={{ width: "100%" }}
                  />
                  <Typography sx={{ fontSize: "0.65rem", color: "#999", mt: 0.3 }}>default 0.60</Typography>
                </Box>
                
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontSize: "0.75rem", color: "#666", mb: 0.5 }}>Nota mínima para aprobar</Typography>
                  <TextField
                    size="small"
                    type="number"
                    value={form.min_passing_grade}
                    onChange={(e) => handleChange("min_passing_grade", parseFloat(e.target.value))}
                    inputProps={{ min: 0, max: 10, step: 0.5 }}
                    sx={{ width: "100%" }}
                  />
                  <Typography sx={{ fontSize: "0.65rem", color: "#999", mt: 0.3 }}>default 8.00</Typography>
                </Box>
              </Box>
            </Box>
          )}

          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, mt: 2 }}>
            <FormControlLabel
              control={<Switch checked={form.activo} onChange={(e) => handleChange("activo", e.target.checked)} size="small" />}
              label={<Typography sx={{ fontSize: "0.85rem" }}>Bloque activo (visible para los voluntarios)</Typography>}
            />
            <FormControlLabel
              control={<Switch checked={form.opcional} onChange={(e) => handleChange("opcional", e.target.checked)} size="small" />}
              label={<Typography sx={{ fontSize: "0.85rem" }}>Bloque opcional (no requerido para completar el programa)</Typography>}
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #f0f0f0", justifyContent: "space-between" }}>
          <Box>
            {!isNew && (
              confirmDelete ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography sx={{ fontSize: "0.78rem", color: "#e60000", fontWeight: 600 }}>¿Confirmar eliminación?</Typography>
                  <Button size="small" variant="contained" onClick={handleDelete} disabled={deleting}
                    sx={{ backgroundColor: "#e60000", color: "#fff", textTransform: "none", fontWeight: 700, fontSize: "0.78rem", "&:hover": { backgroundColor: "#b30000" } }}>
                    {deleting ? <CircularProgress size={13} sx={{ color: "#fff" }} /> : "Sí, eliminar"}
                  </Button>
                  <Button size="small" onClick={() => setConfirmDelete(false)}
                    sx={{ textTransform: "none", fontSize: "0.78rem", color: "#777" }}>Cancelar</Button>
                </Box>
              ) : (
                <Tooltip title="Eliminar bloque">
                  <Button size="small" startIcon={<FaTrash size={11} />} onClick={handleDelete}
                    sx={{ textTransform: "none", fontWeight: 600, color: "#cc0000", border: "1px solid #ffd0d0", borderRadius: 1.5, fontSize: "0.82rem", "&:hover": { backgroundColor: "#fff5f5", borderColor: "#e60000" } }}>
                    Eliminar
                  </Button>
                </Tooltip>
              )
            )}
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button variant="outlined" startIcon={<FaEye size={12} />} onClick={() => setPreviewOpen(true)}
              sx={{ textTransform: "none", fontWeight: 600, color: "#555", borderColor: "#ddd", fontSize: "0.85rem" }}>
              VISTA PREVIA
            </Button>
            <Button variant="contained" onClick={handleSave} disabled={saving}
              sx={{ textTransform: "none", fontWeight: 700, backgroundColor: "#e60000", color: "#fff", fontSize: "0.85rem", "&:hover": { backgroundColor: "#b30000" } }}>
              {saving && <CircularProgress size={16} sx={{ color: "#fff", mr: 1 }} />}
              ✓ CONFIRMAR Y GUARDAR
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pb: 1, pt: 2, px: 3, borderBottom: "1px solid #f0f0f0" }}>
          <Typography sx={{ fontWeight: 800, fontSize: "1rem", color: "#111" }}>
            👁️ Vista previa — {form.title || "Sin título"}
          </Typography>
          <IconButton onClick={() => setPreviewOpen(false)} size="small" sx={{ color: "#999" }}>
            <FaTimes size={14} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 3, py: 2.5 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Chip label={form.code || `BLOQUE`} size="small" sx={{ alignSelf: "flex-start", backgroundColor: "#fff0f0", color: "#b30000", fontWeight: 700 }} />
            <Typography sx={{ fontWeight: 800, fontSize: "1.1rem", color: "#111" }}>{form.title || "Sin título"}</Typography>
            {form.descripcion && <Typography sx={{ fontSize: "0.88rem", color: "#555" }}>{form.descripcion}</Typography>}
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              <Typography sx={{ fontSize: "0.8rem", color: "#888" }}>📌 Orden: {form.orden}</Typography>
              {form.duracion_estimada && <Typography sx={{ fontSize: "0.8rem", color: "#888" }}>⏱️ {form.duracion_estimada} min</Typography>}
            </Box>
            {form.opcional && <Chip label="🔓 Bloque opcional" size="small" sx={{ alignSelf: "flex-start", backgroundColor: "#e8f5e9", color: "#2e7d32" }} />}
            {!form.activo && <Chip label="Bloque inactivo" size="small" sx={{ alignSelf: "flex-start", backgroundColor: "#fce4ec", color: "#c62828" }} />}
            {form.has_practical_exam && <Chip label="📋 Tiene examen práctico" size="small" sx={{ alignSelf: "flex-start", backgroundColor: "#e3f2fd", color: "#1565c0" }} />}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #f0f0f0" }}>
          <Button variant="outlined" onClick={() => setPreviewOpen(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
// -----------------------FIN MODAL EDITAR BLOQUE, CREAR BLOQUE---------------------------------------------------------------//

//------------------------INICIO MODAL EDITAR BORRAR CREAR MODULO----------------------------------------------------------//
function ModalEditarModulo({ open, onClose, modulo, bloqueId, onSaved }) {
  const isNew = !modulo;

  const [form, setForm] = useState({
    code: "", title: "", descripcion: "", orden: "", duracion_estimada: "",
    fecha_inicio: "", fecha_cierre: "", activo: true, es_examen_final: false,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false); 
  const [previewOpen, setPreviewOpen] = useState(false);

  useEffect(() => {
    if (modulo) {
      setForm({
        code: modulo.code || modulo.codigo || "",
        title: modulo.title || modulo.nombre || "",
        descripcion: modulo.description || modulo.descripcion || "",
        orden: modulo.order ?? modulo.orden ?? "",
        duracion_estimada: modulo.estimated_minutes ?? modulo.duracion_estimada ?? "",
        fecha_inicio: modulo.start_date || modulo.fecha_inicio || "",
        fecha_cierre: modulo.end_date || modulo.fecha_cierre || "",
        activo: modulo.is_active !== undefined ? !!modulo.is_active : modulo.activo !== false,
        es_examen_final: !!modulo.is_final_exam || !!modulo.es_examen_final,
      });
    } else {
      setForm({ code: "", title: "", descripcion: "", orden: "", duracion_estimada: "", fecha_inicio: "", fecha_cierre: "", activo: true, es_examen_final: false });
    }
    setError(null);
  }, [modulo, open]);

  const handleChange = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  const handleSave = async () => {
    if (!form.title.trim()) { setError("El nombre del módulo es requerido."); return; }
    if (isNew && !form.code.trim()) { setError("El código del módulo es requerido."); return; }

    setSaving(true);
    setError(null);

    try {
      const payload = {
        code: form.code || undefined,
        title: form.title,
        description: form.descripcion,
        order: form.orden !== "" ? Number(form.orden) : undefined,
        estimatedMinutes: form.duracion_estimada !== "" ? Number(form.duracion_estimada) : undefined,
        startDate: form.fecha_inicio || undefined,
        endDate: form.fecha_cierre || undefined,
        isActive: form.activo,
        isFinalExam: form.es_examen_final,
      };

      if (isNew) {
        if (!bloqueId) throw new Error("No se ha especificado un bloque para este módulo");
        await authedFetch(`/admin/catalog/blocks/${bloqueId}/modules`, {
          method: "POST",
          body: JSON.stringify(payload),
        });
      } else {
        if (!modulo?.id) throw new Error("No se ha especificado el ID del módulo a editar");
        await authedFetch(`/admin/catalog/modules/${modulo.id}`, {
          method: "PATCH",
          body: JSON.stringify(payload),
        });
      }
      onSaved?.();
      onClose();
    } catch (err) {
      console.error("❌ Error:", err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) { setConfirmDelete(true); return; }
    setDeleting(true);
    setError(null);
    try {
      await authedFetch(`/admin/catalog/modules/${modulo.id}`, { method: "DELETE" });
      onSaved?.();
      onClose();
    } catch (err) {
      setError(err.message);
      setConfirmDelete(false);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3, maxHeight: "90vh" } }}>
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pb: 1, pt: 2.5, px: 3, borderBottom: "1px solid #f0f0f0" }}>
          <Typography sx={{ fontWeight: 800, fontSize: "1.1rem", color: "#111" }}>
            {isNew ? "Nuevo Módulo" : "Editar Módulo"}
          </Typography>
          <IconButton onClick={onClose} size="small" sx={{ color: "#999" }}><FaTimes size={14} /></IconButton>
        </DialogTitle>

        <DialogContent sx={{ px: 3, py: 2.5, overflowY: "auto" }}>
          <Alert severity="info" icon={<span style={{ fontSize: "1rem" }}>💡</span>}
            sx={{ mb: 2.5, backgroundColor: "#fffbea", color: "#555", border: "1px solid #ffe082", borderRadius: 2, fontSize: "0.8rem", "& .MuiAlert-message": { lineHeight: 1.5 } }}>
            Un <strong>módulo</strong> es un tema específico dentro del bloque y contiene las actividades.
          </Alert>

          {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}

          <TextField label="Código del módulo *" fullWidth size="small" value={form.code}
            onChange={(e) => handleChange("code", e.target.value)}
            helperText="Sigue la convención del programa: M01, M02, M03…" sx={{ mb: 2 }} />

          <TextField label="Nombre del módulo *" fullWidth size="small" value={form.title}
            onChange={(e) => handleChange("title", e.target.value)}
            helperText="Nombre visible del módulo para el voluntario." sx={{ mb: 2 }} />

          <TextField label="Descripción" fullWidth size="small" multiline rows={2} value={form.descripcion}
            onChange={(e) => handleChange("descripcion", e.target.value)}
            helperText="Opcional. Descripción breve de qué cubre este módulo." sx={{ mb: 2 }} />

          <TextField label="Orden" fullWidth size="small" type="number" value={form.orden}
            onChange={(e) => handleChange("orden", e.target.value)}
            helperText="Posición dentro del bloque. Dejar vacío para agregar al final." sx={{ mb: 2 }} />

          <TextField label="Duración estimada (minutos)" fullWidth size="small" type="number"
            value={form.duracion_estimada} onChange={(e) => handleChange("duracion_estimada", e.target.value)}
            helperText="Tiempo aproximado para completar todas las actividades del módulo." sx={{ mb: 2 }} />

          <TextField label="Fecha de inicio" fullWidth size="small" type="datetime-local"
            value={form.fecha_inicio} onChange={(e) => handleChange("fecha_inicio", e.target.value)}
            helperText="Opcional. El módulo se habilita desde esta fecha."
            InputLabelProps={{ shrink: true }} sx={{ mb: 2 }} />

          <TextField label="Fecha de cierre" fullWidth size="small" type="datetime-local"
            value={form.fecha_cierre} onChange={(e) => handleChange("fecha_cierre", e.target.value)}
            helperText="Opcional. El módulo se deshabilita después de esta fecha."
            InputLabelProps={{ shrink: true }} sx={{ mb: 2 }} />

          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <FormControlLabel
              control={<Switch checked={form.activo} onChange={(e) => handleChange("activo", e.target.checked)} size="small" />}
              label={<Typography sx={{ fontSize: "0.85rem" }}>Módulo activo (visible para los voluntarios)</Typography>}
            />
            <FormControlLabel
              control={<Switch checked={form.es_examen_final} onChange={(e) => handleChange("es_examen_final", e.target.checked)} size="small" />}
              label={<Typography sx={{ fontSize: "0.85rem" }}>Es examen final (bloquea avance hasta aprobarlo)</Typography>}
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #f0f0f0", justifyContent: "space-between", gap: 1 }}>
          <Box>
            {!isNew && (
              confirmDelete ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography sx={{ fontSize: "0.78rem", color: "#e60000", fontWeight: 600 }}>¿Confirmar eliminación?</Typography>
                  <Button size="small" variant="contained" onClick={handleDelete} disabled={deleting}
                    sx={{ backgroundColor: "#e60000", color: "#fff", textTransform: "none", fontWeight: 700, fontSize: "0.78rem", "&:hover": { backgroundColor: "#b30000" } }}>
                    {deleting ? <CircularProgress size={13} sx={{ color: "#fff" }} /> : "Sí, eliminar"}
                  </Button>
                  <Button size="small" onClick={() => setConfirmDelete(false)}
                    sx={{ textTransform: "none", fontSize: "0.78rem", color: "#777" }}>Cancelar</Button>
                </Box>
              ) : (
                <Tooltip title="Eliminar módulo">
                  <Button size="small" startIcon={<FaTrash size={11} />} onClick={handleDelete}
                    sx={{ textTransform: "none", fontWeight: 600, color: "#cc0000", border: "1px solid #ffd0d0", borderRadius: 1.5, fontSize: "0.82rem", "&:hover": { backgroundColor: "#fff5f5", borderColor: "#e60000" } }}>
                    Eliminar
                  </Button>
                </Tooltip>
              )
            )}
          </Box>

          <Box sx={{ display: "flex", gap: 1 }}>
            <Button variant="outlined" startIcon={<FaEye size={12} />} onClick={() => setPreviewOpen(true)}
              sx={{ textTransform: "none", fontWeight: 600, color: "#555", borderColor: "#ddd", fontSize: "0.85rem" }}>
              VISTA PREVIA
            </Button>
            <Button variant="contained" onClick={handleSave} disabled={saving}
              sx={{ textTransform: "none", fontWeight: 700, backgroundColor: "#e60000", color: "#fff", fontSize: "0.85rem", "&:hover": { backgroundColor: "#b30000" } }}>
              {saving && <CircularProgress size={16} sx={{ color: "#fff", mr: 1 }} />}
              ✓ CONFIRMAR Y GUARDAR
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Diálogo de vista previa */}
      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3, maxHeight: "90vh" } }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pb: 1, pt: 2, px: 3, borderBottom: "1px solid #f0f0f0" }}>
          <Typography sx={{ fontWeight: 800, fontSize: "1rem", color: "#111" }}>
            👁️ Vista previa — {form.title || "Sin título"}
          </Typography>
          <IconButton onClick={() => setPreviewOpen(false)} size="small" sx={{ color: "#999" }}>
            <FaTimes size={14} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 3, py: 2.5 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {form.code && <Chip label={form.code} size="small" sx={{ alignSelf: "flex-start", backgroundColor: "#f3e8ff", color: "#7c3aed", fontWeight: 700 }} />}
            <Typography sx={{ fontWeight: 800, fontSize: "1.1rem", color: "#111" }}>{form.title || "Sin título"}</Typography>
            {form.descripcion && <Typography sx={{ fontSize: "0.88rem", color: "#555" }}>{form.descripcion}</Typography>}
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              {form.orden && <Typography sx={{ fontSize: "0.8rem", color: "#888" }}>📌 Orden: {form.orden}</Typography>}
              {form.duracion_estimada && <Typography sx={{ fontSize: "0.8rem", color: "#888" }}>⏱️ {form.duracion_estimada} min</Typography>}
            </Box>
            {form.es_examen_final && <Chip label="⚠️ Examen final" size="small" sx={{ alignSelf: "flex-start", backgroundColor: "#fff3e0", color: "#e65100", fontWeight: 700 }} />}
            {!form.activo && <Chip label="Módulo inactivo" size="small" sx={{ alignSelf: "flex-start", backgroundColor: "#fce4ec", color: "#c62828" }} />}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #f0f0f0" }}>
          <Button variant="outlined" onClick={() => setPreviewOpen(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
//---------------------------------------------FINAL DE MODAL EDITAR BORRAR CREAR MODULO-------------------------------------//
//---------------------------INICIO DE Modal: Editar / Crear Programa -------------------------------------------------------//
function ModalEditarPrograma({ open, onClose, programa, onSaved }) {
  const isNew = !programa;

  const [form, setForm] = useState({
    code: "", name: "", descripcion: "", tipo: "", nivel: "",
    duracion_estimada: "", imagen_url: "", etiquetas: "", activo: true,
  });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  useEffect(() => {
    if (programa) {
      setForm({
        code: programa.code || programa.codigo || "",
        name: programa.name || programa.nombre || programa.title || "",
        descripcion: programa.description || programa.descripcion || "",
        tipo: programa.formacion || programa.tipo || programa.type || "",
        nivel: programa.level || programa.nivel || "",
        duracion_estimada: programa.estimated_minutes ?? programa.duracion_estimada ?? "",
        imagen_url: programa.image || programa.imagen_url || programa.cover_url || "",
        etiquetas: Array.isArray(programa.tags) ? programa.tags.join(", ") : programa.tags || programa.etiquetas || "",
        activo: programa.is_active !== undefined ? !!programa.is_active : programa.activo !== false,
      });
    } else {
      setForm({ code: "", name: "", descripcion: "", tipo: "", nivel: "", duracion_estimada: "", imagen_url: "", etiquetas: "", activo: true });
    }
    setError(null);
    setConfirmDelete(false);
  }, [programa, open]);

  const handleChange = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  const handleSave = async () => {
    if (!form.name.trim()) { setError("El nombre del programa es requerido."); return; }
    if (isNew && !form.code.trim()) { setError("El código del programa es requerido."); return; }
    setSaving(true);
    setError(null);
    try {
      // ✅ FIX: nombres de campo correctos para el backend
      const payload = {
        code: isNew ? form.code : undefined,
        name: form.name,
        description: form.descripcion,
        formacion: form.tipo || undefined,            // ✅ era "tipo"
        level: form.nivel || undefined,               // ✅ era "nivel"
        estimatedMinutes: form.duracion_estimada !== "" ? Number(form.duracion_estimada) : undefined,
        image: form.imagen_url || undefined,          // ✅ era "imagen_url"
        tags: form.etiquetas ? form.etiquetas.split(",").map((t) => t.trim()).filter(Boolean) : [],
        isActive: form.activo,                        // ✅ era "activo"
      };

      if (isNew) {
        await authedFetch(`/admin/catalog/programs`, { method: "POST", body: JSON.stringify(payload) });
      } else {
        await authedFetch(`/admin/catalog/programs/${programa.code || programa.codigo}`, {
          method: "PATCH",
          body: JSON.stringify(payload),
        });
      }
      onSaved?.();
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) { setConfirmDelete(true); return; }
    setDeleting(true);
    setError(null);
    try {
      await authedFetch(`/admin/catalog/programs/${programa.code || programa.codigo}`, { method: "DELETE" });
      onSaved?.();
      onClose();
    } catch (err) {
      setError(err.message);
      setConfirmDelete(false);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3, maxHeight: "90vh" } }}>
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pb: 1, pt: 2.5, px: 3, borderBottom: "1px solid #f0f0f0" }}>
        <Typography sx={{ fontWeight: 800, fontSize: "1.1rem", color: "#111" }}>
          {isNew ? "Nuevo Programa" : "Editar Programa"}
        </Typography>
        <IconButton onClick={onClose} size="small" sx={{ color: "#999" }}><FaTimes size={14} /></IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 3, py: 2.5, overflowY: "auto" }}>
        <Alert severity="info" icon={<span style={{ fontSize: "1rem" }}>💡</span>}
          sx={{ mb: 2.5, backgroundColor: "#fffbea", color: "#555", border: "1px solid #ffe082", borderRadius: 2, fontSize: "0.8rem", "& .MuiAlert-message": { lineHeight: 1.5 } }}>
          El <strong>código</strong> es el identificador interno del programa. El <strong>nombre</strong> sí aparece en la plataforma.
        </Alert>

        {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}

        {isNew && (
          <TextField label="Código del programa *" fullWidth size="small" value={form.code}
            onChange={(e) => handleChange("code", e.target.value.toUpperCase().replace(/\s/g, ""))}
            helperText="Corto, sin espacios, todo en mayúsculas." sx={{ mb: 2 }} />
        )}

        <TextField label="Nombre del programa *" fullWidth size="small" value={form.name}
          onChange={(e) => handleChange("name", e.target.value)}
          helperText="Nombre completo visible para el voluntario." sx={{ mb: 2 }} />

        <TextField label="Descripción" fullWidth size="small" multiline rows={3} value={form.descripcion}
          onChange={(e) => handleChange("descripcion", e.target.value)}
          helperText="Explica brevemente de qué trata el programa y a quién va dirigido." sx={{ mb: 2 }} />

        <TextField label="Tipo de formación" fullWidth size="small" value={form.tipo}
          onChange={(e) => handleChange("tipo", e.target.value)}
          helperText="Categoría interna del programa. Usa camelCase sin espacios." sx={{ mb: 2 }} />

        <TextField label="Nivel" fullWidth size="small" value={form.nivel}
          onChange={(e) => handleChange("nivel", e.target.value)}
          helperText="Nivel de dificultad o requisito previo del programa." sx={{ mb: 2 }} />

        <TextField label="Duración estimada (minutos)" fullWidth size="small" type="number"
          value={form.duracion_estimada} onChange={(e) => handleChange("duracion_estimada", e.target.value)}
          helperText="Tiempo total aproximado para completar el programa." sx={{ mb: 2 }} />

        <TextField label="URL de imagen de portada" fullWidth size="small" value={form.imagen_url}
          onChange={(e) => handleChange("imagen_url", e.target.value)}
          helperText="Imagen que se muestra como portada del programa." sx={{ mb: 2 }} />

        <TextField label="Etiquetas" fullWidth size="small" value={form.etiquetas}
          onChange={(e) => handleChange("etiquetas", e.target.value)}
          helperText="Palabras clave separadas por coma." sx={{ mb: 2 }} />

        <FormControlLabel
          control={<Switch checked={form.activo} onChange={(e) => handleChange("activo", e.target.checked)} size="small" />}
          label={<Typography sx={{ fontSize: "0.85rem" }}>Programa activo (visible para los voluntarios)</Typography>}
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #f0f0f0", justifyContent: "space-between", gap: 1 }}>
        <Box>
          {!isNew && (
            confirmDelete ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography sx={{ fontSize: "0.78rem", color: "#e60000", fontWeight: 600 }}>¿Confirmar eliminación?</Typography>
                <Button size="small" variant="contained" onClick={handleDelete} disabled={deleting}
                  sx={{ backgroundColor: "#e60000", color: "#fff", textTransform: "none", fontWeight: 700, fontSize: "0.78rem", "&:hover": { backgroundColor: "#b30000" } }}>
                  {deleting ? <CircularProgress size={13} sx={{ color: "#fff" }} /> : "Sí, eliminar"}
                </Button>
                <Button size="small" onClick={() => setConfirmDelete(false)}
                  sx={{ textTransform: "none", fontSize: "0.78rem", color: "#777" }}>Cancelar</Button>
              </Box>
            ) : (
              <Tooltip title="Eliminar programa">
                <Button size="small" startIcon={<FaTrash size={11} />} onClick={handleDelete}
                  sx={{ textTransform: "none", fontWeight: 600, color: "#cc0000", border: "1px solid #ffd0d0", borderRadius: 1.5, fontSize: "0.82rem", "&:hover": { backgroundColor: "#fff5f5", borderColor: "#e60000" } }}>
                  Eliminar
                </Button>
              </Tooltip>
            )
          )}
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button variant="outlined" startIcon={<FaEye size={12} />} onClick={() => setPreviewOpen(true)}
            sx={{ textTransform: "none", fontWeight: 600, color: "#555", borderColor: "#ddd", fontSize: "0.85rem" }}>
            VISTA PREVIA
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}
            sx={{ textTransform: "none", fontWeight: 700, backgroundColor: "#e60000", color: "#fff", fontSize: "0.85rem", "&:hover": { backgroundColor: "#b30000" } }}>
            {saving && <CircularProgress size={16} sx={{ color: "#fff", mr: 1 }} />}
            ✓ CONFIRMAR Y GUARDAR
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}

// ─── Sub-component: fila de actividad ───────────────────────────────────────
function ActividadRow({ actividad, index, onEdit, onDelete }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pl: 5, pr: 2, py: 0.9, borderBottom: "1px solid #f5f0fa", "&:last-child": { borderBottom: "none" }, "&:hover": { backgroundColor: "#fdf8ff" } }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Typography sx={{ fontSize: "0.8rem" }}>{getTipoIcon(actividad.type || actividad.tipo)}</Typography>
        <Typography sx={{ fontSize: "0.82rem", color: "#555" }}>
          {actividad.title || actividad.nombre || `Actividad ${index + 1}`}
        </Typography>
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Chip label={actividad.type || actividad.tipo || "—"} size="small"
          sx={{ backgroundColor: "#f3f3f3", color: "#888", fontSize: "0.65rem", height: 18 }} />
        {actividad.es_examen_final && (
          <Chip label="Examen" size="small" sx={{ backgroundColor: "#ff9800", color: "#fff", fontWeight: 700, fontSize: "0.65rem", height: 18 }} />
        )}
        <Tooltip title="Editar actividad">
          <IconButton size="small" onClick={() => onEdit(actividad)} sx={{ color: "#e60000", "&:hover": { color: "#b30000" } }}>
            <FaPencilAlt size={11} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Eliminar actividad">
          <IconButton size="small" onClick={() => onDelete(actividad)} sx={{ color: "#ccc", "&:hover": { color: "#e60000" } }}>
            <FaTrash size={11} />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}

// ─── Sub-component: fila de módulo ──────────────────────────────────────────
function ModuloRow({ modulo, index, onEditActividad, onNewActividad, onDeleteActividad, onEditModulo }) {
  const [open, setOpen] = useState(false);
  const actividades = modulo.activities || modulo.actividades || [];

  return (
    <Box sx={{ borderBottom: "1px solid #f0e8f5", "&:last-child": { borderBottom: "none" } }}>
      <Box onClick={() => setOpen((o) => !o)}
        sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pl: 3, pr: 2, py: 1.1, cursor: "pointer", "&:hover": { backgroundColor: "#fdf8ff" } }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <MdCheckBoxOutlineBlank size={16} color="#ccc" />
          <Typography sx={{ fontSize: "0.875rem", color: "#333" }}>
            {modulo.order ?? index + 1}.{" "}{modulo.title || modulo.nombre || modulo.name || `Módulo ${index + 1}`}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
          <Chip label={modulo.code || modulo.codigo || `M${String(index + 1).padStart(2, "0")}`} size="small"
            sx={{ backgroundColor: "#f3e8ff", color: "#7c3aed", fontWeight: 600, fontSize: "0.68rem", height: 20 }} />
          <Tooltip title="Editar módulo">
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); onEditModulo(modulo); }}
              sx={{ color: "#e60000", "&:hover": { color: "#b30000" } }}>
              <FaPencilAlt size={11} />
            </IconButton>
          </Tooltip>
          <IconButton size="small" sx={{ color: "#ccc" }}>
            {open ? <FaChevronUp size={11} /> : <FaChevronDown size={11} />}
          </IconButton>
        </Box>
      </Box>
      <Collapse in={open}>
        <Box>
          {actividades.map((act, aIdx) => (
            <ActividadRow key={act.id || aIdx} actividad={act} index={aIdx}
              onEdit={onEditActividad} onDelete={onDeleteActividad} />
          ))}
          <Box sx={{ pl: 5, pr: 2, py: 0.9 }}>
            <Button size="small" startIcon={<FaPlus size={10} />} onClick={() => onNewActividad(modulo)}
              sx={{ color: "#e60000", textTransform: "none", fontWeight: 600, fontSize: "0.78rem" }}>
              AGREGAR ACTIVIDAD
            </Button>
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
}

// ─── Sub-component: fila de bloque ──────────────────────────────────────────
function BloqueRow({ bloque, bloqueIndex, programaCode, onEditActividad, onNewActividad, onDeleteActividad, onEditModulo, onNewModulo, onEditBloque, refreshKey }) {
  const [open, setOpen] = useState(bloqueIndex === 0);
  const modulos = bloque.modules || bloque.modulos || bloque.activities || [];

  return (
    <Box sx={{ mb: 1.5, border: "1px solid #e8dff5", borderRadius: 2, overflow: "hidden" }}>
      <Box onClick={() => setOpen((o) => !o)}
        sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2.5, py: 1.4, backgroundColor: "#fdf4f4", cursor: "pointer", "&:hover": { backgroundColor: "#fce8e8" } }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Typography sx={{ fontSize: "0.9rem", fontWeight: 700, color: "#222" }}>
            📋 Bloque {bloqueIndex + 1}: {bloque.title || bloque.nombre || bloque.name}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Chip label={bloque.code || bloque.codigo || `B${String(bloqueIndex + 1).padStart(2, "0")}`} size="small"
            sx={{ backgroundColor: "#fff0f0", color: "#b30000", fontWeight: 700, fontSize: "0.68rem", height: 20 }} />
          <Tooltip title="Editar bloque">
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); onEditBloque(bloque); }}
              sx={{ color: "#e60000", "&:hover": { color: "#b30000" } }}>
              <FaPencilAlt size={11} />
            </IconButton>
          </Tooltip>
          <IconButton size="small" sx={{ color: "#999" }}>
            {open ? <FaChevronUp size={13} /> : <FaChevronDown size={13} />}
          </IconButton>
        </Box>
      </Box>
      <Collapse in={open}>
        <Box>
          {modulos.length === 0 ? (
            <Typography sx={{ px: 3, py: 1.5, fontSize: "0.8rem", color: "#aaa" }}>Sin módulos registrados.</Typography>
          ) : (
            modulos.map((mod, idx) => (
              <ModuloRow key={mod.id || idx} modulo={mod} index={idx}
                onEditActividad={onEditActividad} onNewActividad={onNewActividad}
                onDeleteActividad={onDeleteActividad} onEditModulo={onEditModulo} />
            ))
          )}
          <Box sx={{ px: 3, py: 1.2, borderTop: "1px dashed #f0e8f5" }}>
            <Button size="small" startIcon={<FaPlus size={10} />} onClick={() => onNewModulo(bloque)}
              sx={{ color: "#e60000", textTransform: "none", fontWeight: 600, fontSize: "0.78rem" }}>
              AGREGAR MÓDULO
            </Button>
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
}

// ─── Sub-component: panel derecho con detalle del programa ──────────────────
function ProgramaDetalle({ programa, onEditActividad, onNewActividad, onDeleteActividad, onEditModulo, onNewModulo, onEditBloque, onNewBloque, onEditPrograma, refreshKey }) {
  const [detalle, setDetalle] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // ── Toggle inscripciones ──────────────────────────────────────────────────
  const [inscAbierta, setInscAbierta] = useState(programa?.inscripcion_abierta !== 0);
  const [toggling, setToggling] = useState(false);

  useEffect(() => {
    setInscAbierta(programa?.inscripcion_abierta !== 0);
  }, [programa]);

  const handleToggleInscripcion = async () => {
    const nuevoValor = !inscAbierta;
    setToggling(true);
    try {
      await authedFetch(`/admin/catalog/programs/${programa.code || programa.codigo}/inscripcion`, {
        method: "PATCH",
        body: JSON.stringify({ inscripcion_abierta: nuevoValor ? 1 : 0 }),
      });
      setInscAbierta(nuevoValor);
    } catch (err) {
      console.error("❌ Error toggle:", err);
      // revertir en caso de error
    } finally {
      setToggling(false);
    }
  };
  // ─────────────────────────────────────────────────────────────────────────

    useEffect(() => {
    if (!programa?.code && !programa?.codigo) return;
    const code = programa.code || programa.codigo;
    setLoading(true);
    setError(null);
    authedFetch(`/admin/catalog/programs/${code}/tree`)
      .then((data) => {
        setDetalle(data);
        // Sincronizar inscAbierta desde el árbol (fuente de verdad en DB)
        if (data?.program?.inscripcion_abierta !== undefined) {
          setInscAbierta(data.program.inscripcion_abierta !== 0);
        }
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [programa, refreshKey]);

  if (!programa) {
    return (
      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", backgroundColor: "#faf6ff", borderRadius: 3, minHeight: 400, gap: 1 }}>
        <Typography sx={{ fontSize: "2rem" }}>🗂️</Typography>
        <Typography sx={{ fontWeight: 700, color: "#555", fontSize: "1.1rem" }}>Selecciona un programa</Typography>
        <Typography sx={{ color: "#aaa", fontSize: "0.85rem" }}>O crea uno nuevo con el botón de arriba.</Typography>
      </Box>
    );
  }

  const code = programa.code || programa.codigo;
  const nombre = programa.name || programa.nombre || programa.title;
  const descripcion = programa.description || programa.descripcion || "";
  const tags = programa.tags || programa.categorias || [];
  const bloques = detalle?.blocks || detalle?.bloques || detalle?.modules || [];

  return (
    <Box sx={{ flex: 1, backgroundColor: "#faf6ff", borderRadius: 3, p: 3, overflowY: "auto" }}>
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: 2, flexWrap: "wrap", gap: 1 }}>
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 0.5, flexWrap: "wrap" }}>
            <Typography sx={{ fontWeight: 800, fontSize: "1.3rem", color: "#111" }}>{nombre}</Typography>
            <Chip label={code} size="small" sx={{ backgroundColor: "#e8f4fd", color: "#1565c0", fontWeight: 700, fontSize: "0.75rem" }} />
            <Chip
              label={inscAbierta ? "✅ Inscripciones abiertas" : "🔒 Inscripciones cerradas"}
              size="small"
              sx={{
                backgroundColor: inscAbierta ? "#e8f5e9" : "#fce4ec",
                color: inscAbierta ? "#2e7d32" : "#c62828",
                fontWeight: 700,
                fontSize: "0.75rem",
              }}
            />
          </Box>
          <Typography sx={{ fontSize: "0.85rem", color: "#666", maxWidth: 500 }}>{descripcion}</Typography>
          {tags.length > 0 && (
            <Box sx={{ display: "flex", gap: 0.8, mt: 1, flexWrap: "wrap" }}>
              {tags.map((t, i) => (
                <Chip key={i} label={t} size="small" sx={{ backgroundColor: "#f3e8ff", color: "#6b21a8", fontSize: "0.7rem", height: 22 }} />
              ))}
            </Box>
          )}
        </Box>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
          {/* ── Toggle inscripciones ── */}
          <Tooltip title={inscAbierta ? "Cerrar inscripciones" : "Abrir inscripciones"}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, px: 1.5, py: 0.8, borderRadius: 2, border: `1px solid ${inscAbierta ? "#c8e6c9" : "#ffcdd2"}`, backgroundColor: inscAbierta ? "#f1f8f1" : "#fff5f5", cursor: toggling ? "default" : "pointer" }}
              onClick={toggling ? undefined : handleToggleInscripcion}
            >
              {toggling
                ? <CircularProgress size={14} sx={{ color: "#999" }} />
                : <Switch
                    checked={inscAbierta}
                    size="small"
                    onChange={handleToggleInscripcion}
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": { color: "#2e7d32" },
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#4caf50" },
                    }}
                  />
              }
              <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, color: inscAbierta ? "#2e7d32" : "#c62828", whiteSpace: "nowrap" }}>
                {inscAbierta ? "Abiertas" : "Cerradas"}
              </Typography>
            </Box>
          </Tooltip>

          <Tooltip title="Ver programa">
            <IconButton sx={{ color: "#999", "&:hover": { color: "#333" } }}><FaEye size={16} /></IconButton>
          </Tooltip>
          <Tooltip title="Editar programa">
            <IconButton onClick={() => onEditPrograma(programa)} sx={{ color: "#e60000", "&:hover": { color: "#b30000" } }}>
              <FaPencilAlt size={16} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Divider sx={{ mb: 2.5 }} />

      {loading && <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}><CircularProgress size={32} sx={{ color: "#e60000" }} /></Box>}
      {error && <Typography sx={{ color: "#e60000", fontSize: "0.85rem", py: 2 }}>Error cargando detalle: {error}</Typography>}
      {!loading && !error && bloques.length === 0 && (
        <Typography sx={{ color: "#aaa", fontSize: "0.85rem", py: 2 }}>Este programa no tiene bloques registrados aún.</Typography>
      )}
      {!loading && !error && bloques.map((bloque, idx) => (
        <BloqueRow key={bloque.id || idx} bloque={bloque} bloqueIndex={idx} programaCode={code}
          onEditActividad={onEditActividad} onNewActividad={onNewActividad}
          onDeleteActividad={onDeleteActividad} onEditModulo={onEditModulo}
          onNewModulo={onNewModulo} onEditBloque={onEditBloque} refreshKey={refreshKey} />
      ))}

      {!loading && !error && (
        <Box sx={{ mt: 2 }}>
          <Button variant="outlined" size="small" startIcon={<FaPlus size={11} />} onClick={() => onNewBloque(programa)}
            sx={{ color: "#e60000", borderColor: "#ffd0d0", textTransform: "none", fontWeight: 700, fontSize: "0.82rem", borderRadius: 2, "&:hover": { backgroundColor: "#fff5f5", borderColor: "#e60000" } }}>
            + AGREGAR BLOQUE
          </Button>
        </Box>
      )}
    </Box>
  );
}

// ─── Componente principal ────────────────────────────────────────────────────
export default function GestionCursos() {
  const [programas, setProgramas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [seleccionado, setSeleccionado] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const [modalActividadOpen, setModalActividadOpen] = useState(false);
  const [actividadEditar, setActividadEditar] = useState(null);
  const [moduloActivo, setModuloActivo] = useState(null);

  const [modalModuloOpen, setModalModuloOpen] = useState(false);
  const [moduloEditar, setModuloEditar] = useState(null);
  const [bloqueActivoModulo, setBloqueActivoModulo] = useState(null);

  const [modalBloqueOpen, setModalBloqueOpen] = useState(false);
  const [bloqueEditar, setBloqueEditar] = useState(null);

  const [modalProgramaOpen, setModalProgramaOpen] = useState(false);
  const [programaEditar, setProgramaEditar] = useState(null);

  const [confirmDeleteDialog, setConfirmDeleteDialog] = useState({ open: false, actividad: null, loading: false });
  const [snack, setSnack] = useState({ open: false, msg: "", severity: "success" });

  const cargarProgramas = useCallback(() => {
    setLoading(true);
    setError(null);
    authedFetch("/admin/catalog/programs")
      .then((data) => {
        const lista = Array.isArray(data) ? data : data.programs || data.programas || data.data || [];
        setProgramas(lista);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { cargarProgramas(); }, [cargarProgramas]);

  const handleEditActividad = (actividad) => { setActividadEditar(actividad); setModuloActivo(null); setModalActividadOpen(true); };
  const handleNewActividad = (modulo) => { setActividadEditar(null); setModuloActivo(modulo); setModalActividadOpen(true); };
  const handleDeleteActividad = (actividad) => { setConfirmDeleteDialog({ open: true, actividad, loading: false }); };

  const handleConfirmDelete = async () => {
    const { actividad } = confirmDeleteDialog;
    setConfirmDeleteDialog((s) => ({ ...s, loading: true }));
    try {
      await authedFetch(`/admin/catalog/activities/${actividad.id}`, { method: "DELETE" });
      setSnack({ open: true, msg: "Actividad eliminada correctamente.", severity: "success" });
      setRefreshKey((k) => k + 1);
    } catch (err) {
      setSnack({ open: true, msg: `Error al eliminar: ${err.message}`, severity: "error" });
    } finally {
      setConfirmDeleteDialog({ open: false, actividad: null, loading: false });
    }
  };

  const handleEditModulo = (modulo) => { setModuloEditar(modulo); setBloqueActivoModulo(null); setModalModuloOpen(true); };
  const handleNewModulo = (bloque) => { setModuloEditar(null); setBloqueActivoModulo(bloque); setModalModuloOpen(true); };
  const handleEditBloque = (bloque) => { setBloqueEditar(bloque); setModalBloqueOpen(true); };
  const handleNewBloque = (prog) => { setBloqueEditar(null); setModalBloqueOpen(true); };
  const handleEditPrograma = (prog) => { setProgramaEditar(prog); setModalProgramaOpen(true); };
  const handleNewPrograma = () => { setProgramaEditar(null); setModalProgramaOpen(true); };

  const handleSaved = (msg = "Guardado correctamente.") => {
    setSnack({ open: true, msg, severity: "success" });
    setRefreshKey((k) => k + 1);
    cargarProgramas();
  };

  return (
    <Box sx={{ backgroundColor: "#ffffff", minHeight: "100vh", pb: 6 }}>
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 2, mb: 3 }}>
        <Box>
          <Typography sx={{ fontSize: "1.6rem", fontWeight: 800, color: "#111", fontFamily: "'Arial Black', sans-serif", letterSpacing: "-0.5px" }}>
            GESTIÓN DE CURSOS
          </Typography>
          <Typography sx={{ color: "#777", fontSize: "0.875rem", mt: 0.3 }}>
            Crea y edita programas de formación. El sistema genera la configuración automáticamente.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<FaPlus size={13} />} onClick={handleNewPrograma}
          sx={{ backgroundColor: "#e60000", color: "#fff", fontWeight: 700, fontSize: "0.85rem", textTransform: "none", px: 2.5, py: 1, borderRadius: 2, boxShadow: "0 4px 12px rgba(230,0,0,0.3)", "&:hover": { backgroundColor: "#b30000" } }}>
          Nuevo Programa
        </Button>
      </Box>

      <Box sx={{ display: "flex", gap: 2.5, alignItems: "flex-start" }}>
        {/* lista izquierda */}
        <Box sx={{ width: 220, flexShrink: 0, backgroundColor: "#fff", borderRadius: 3, border: "1px solid #eee", overflow: "hidden" }}>
          <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid #eee", backgroundColor: "#fafafa" }}>
            <Typography sx={{ fontWeight: 700, fontSize: "0.85rem", color: "#444" }}>Programas</Typography>
          </Box>
          {loading && <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}><CircularProgress size={28} sx={{ color: "#e60000" }} /></Box>}
          {error && <Typography sx={{ px: 2, py: 2, fontSize: "0.8rem", color: "#e60000" }}>{error}</Typography>}
          {!loading && !error && programas.length === 0 && (
            <Typography sx={{ px: 2, py: 2, fontSize: "0.8rem", color: "#aaa" }}>Sin programas registrados.</Typography>
          )}
          {!loading && !error && programas.map((prog) => {
            const code = prog.code || prog.codigo;
            const nombre = prog.name || prog.nombre || prog.title;
            const isActive = seleccionado && (seleccionado.code || seleccionado.codigo) === code;
            return (
              <Box key={code} onClick={() => setSeleccionado(prog)}
                sx={{ px: 2, py: 1.4, borderBottom: "1px solid #f5f5f5", cursor: "pointer", backgroundColor: isActive ? "#fff5f5" : "transparent", borderLeft: isActive ? "3px solid #e60000" : "3px solid transparent", transition: "0.15s", "&:hover": { backgroundColor: "#fff5f5" } }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Typography sx={{ fontWeight: 700, fontSize: "0.875rem", color: isActive ? "#e60000" : "#222" }}>{code}</Typography>
                  <Chip
                    label={prog.inscripcion_abierta !== 0 ? "Abierto" : "Cerrado"}
                    size="small"
                    sx={{
                      backgroundColor: prog.inscripcion_abierta !== 0 ? "#e8f5e9" : "#fce4ec",
                      color: prog.inscripcion_abierta !== 0 ? "#2e7d32" : "#c62828",
                      fontWeight: 600, fontSize: "0.65rem", height: 20
                    }}
                  />
                </Box>
                <Typography sx={{ fontSize: "0.75rem", color: "#888", mt: 0.2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 160 }}>
                  {nombre}
                </Typography>
              </Box>
            );
          })}
        </Box>

        {/* panel derecho */}
        <ProgramaDetalle
          programa={seleccionado}
          onEditActividad={handleEditActividad} onNewActividad={handleNewActividad}
          onDeleteActividad={handleDeleteActividad} onEditModulo={handleEditModulo}
          onNewModulo={handleNewModulo} onEditBloque={handleEditBloque}
          onNewBloque={handleNewBloque} onEditPrograma={handleEditPrograma}
          refreshKey={refreshKey}
        />
      </Box>

      <ModalEditarPrograma open={modalProgramaOpen} onClose={() => setModalProgramaOpen(false)}
        programa={programaEditar} onSaved={() => handleSaved(programaEditar ? "Programa actualizado." : "Programa creado.")} />

      <ModalEditarBloque open={modalBloqueOpen} onClose={() => setModalBloqueOpen(false)}
        bloque={bloqueEditar} programaCode={seleccionado?.code || seleccionado?.codigo}
        onSaved={() => handleSaved(bloqueEditar ? "Bloque actualizado." : "Bloque creado.")} />

      <ModalEditarModulo open={modalModuloOpen} onClose={() => setModalModuloOpen(false)}
        modulo={moduloEditar} bloqueId={bloqueActivoModulo?.id}
        onSaved={() => handleSaved(moduloEditar ? "Módulo actualizado." : "Módulo creado.")} />

      <ModalEditarActividad open={modalActividadOpen} onClose={() => setModalActividadOpen(false)}
        actividad={actividadEditar} moduloId={moduloActivo?.id ?? moduloActivo?.module_id}
        onSaved={() => handleSaved("Actividad guardada correctamente.")} />

      <Dialog
        open={confirmDeleteDialog.open}
        onClose={() => !confirmDeleteDialog.loading && setConfirmDeleteDialog({ open: false, actividad: null, loading: false })}
        maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ pb: 1, pt: 2.5, px: 3, fontWeight: 800, fontSize: "1rem", color: "#111" }}>
          ¿Eliminar actividad?
        </DialogTitle>
        <DialogContent sx={{ px: 3, pb: 1 }}>
          <Typography sx={{ fontSize: "0.88rem", color: "#555" }}>
            Estás a punto de eliminar <strong>"{confirmDeleteDialog.actividad?.title || confirmDeleteDialog.actividad?.nombre}"</strong>. Esta acción no se puede deshacer.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button onClick={() => setConfirmDeleteDialog({ open: false, actividad: null, loading: false })}
            disabled={confirmDeleteDialog.loading} sx={{ textTransform: "none", color: "#777", fontSize: "0.85rem" }}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={handleConfirmDelete} disabled={confirmDeleteDialog.loading}
            sx={{ backgroundColor: "#e60000", color: "#fff", textTransform: "none", fontWeight: 700, fontSize: "0.85rem", "&:hover": { backgroundColor: "#b30000" } }}>
            {confirmDeleteDialog.loading && <CircularProgress size={15} sx={{ color: "#fff", mr: 1 }} />}
            Sí, eliminar
          </Button>
        </DialogActions>
        

      </Dialog>      
      <Snackbar open={snack.open} autoHideDuration={3500} onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert onClose={() => setSnack((s) => ({ ...s, open: false }))} severity={snack.severity}
          sx={{ borderRadius: 2, fontWeight: 600 }}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  
  );
}
